/**
 * THE ENTITY PAGE'S RULES — in one place, asked by the form AND the entity chat (ADR 0009, ADR 0008).
 *
 * Pure: no Angular, no I/O. Every message is the form's own words, verbatim — the form alerts them, the
 * chat quotes them. Quirks the form has today are KEPT on purpose and logged in
 * `.scratch/zs-entity/followups.md` (N1–N7); fixing one is an owner decision, not a refactor.
 *
 * ⚠ `import type` only. `entity.model` pulls in `base.model`, which imports `@angular/fire` — a value
 * import here would drag Firebase into every node test bundle.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';
import type { Crud, PermissionChange } from '../zs-entity.types';
import { isReservedFieldName, reservedFieldNameMessage } from '../../../../shared/entity/reserved-field-names.util';
import { isSystemVariableKey } from '../../../../shared/components/atlas/atlas.utils';

/** A rule's answer. `reason` is the form's own message, shown verbatim. */
export type NameCheck = { ok: true } | { ok: false; reason: string };
const OK: NameCheck = { ok: true };
const refuse = (reason: string): NameCheck => ({ ok: false, reason });

// ── Field names ─────────────────────────────────────────────────────────────────────────────────────

export const FIELD_NAME_SYSTEM_MESSAGE = 'Variable name cannot contain the word "system"';

/** What the name box does on every keystroke: letters and `_`, and the word "system" removed. */
export function cleanFieldNameAsTyped(raw: string): string {
  return raw.replace(/[^a-zA-Z_]/g, '').replace(/system/gi, '');
}

/** ⚠ N1 — Add strips the underscore that typing kept. */
export function cleanFieldNameOnAdd(raw: string): string {
  return raw.replace(/[^a-zA-Z]/g, '');
}

/** Rename (New entities only) keeps the underscore. */
export function cleanFieldNameOnEdit(raw: string): string {
  return raw.replace(/[^a-zA-Z_]/g, '');
}

/** Add: system word → reserved → duplicate among the container's fields, in that order. */
export function checkNewFieldName(name: string, siblings: readonly Pick<TypeData, 'name'>[]): NameCheck {
  if (name.toLowerCase().includes('system')) return refuse(FIELD_NAME_SYSTEM_MESSAGE);
  if (isReservedFieldName(name)) return refuse(reservedFieldNameMessage(name));
  // ⚠ N5 — an EMPTY name lands here too: the form's duplicate check treats '' as taken.
  if (!name || siblings.some((d) => d.name === name)) return refuse('Same variable name already exists, please change');
  return OK;
}

/** Rename: the system word always; reserved and duplicate only when the name actually changed. */
export function checkRenamedFieldName(
  name: string,
  currentName: string,
  siblings: readonly Pick<TypeData, 'name'>[],
  editingIndex: number,
): NameCheck {
  if (name.toLowerCase().includes('system')) return refuse(FIELD_NAME_SYSTEM_MESSAGE);
  if (name !== currentName) {
    if (isReservedFieldName(name)) return refuse(reservedFieldNameMessage(name));
    if (siblings.some((item, idx) => item.name === name && idx !== editingIndex)) {
      return refuse('A variable with this name already exists');
    }
  }
  return OK;
}

// ── Entity names ────────────────────────────────────────────────────────────────────────────────────

/** ⚠ N2 — what the name box does: letters and digits only, the words system and ootb removed. */
export function cleanEntityNameAsTyped(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '').replace(/system/gi, '').replace(/ootb/gi, '');
}

/** ⚠ N2 — what Save does: keeps `_`. A name set without typing must go through the as-typed cleanup first. */
export function cleanEntityNameOnSave(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_]/g, '');
}

/**
 * Was (2026-10-02): "⚠ N3 — no duplicate check: the form's own check can never fail (see the ledger)."
 * N3 is fixed (owner Q34, 2026-10-03) by `entityNameTaken`, which Save calls after this check.
 */
export function checkEntityNameOnSave(name: string): NameCheck {
  if (!name.trim()) return refuse('Entity name is required');
  if (name.toLowerCase().includes('system')) return refuse('Name cannot contain "system"');
  return OK;
}

/** The form's words when a new entity's name is already in use. */
export const ENTITY_NAME_TAKEN_MESSAGE = 'Same Data Model name already exists, please change';

/**
 * N3 FIXED (owner Q34, 2026-10-03): a new entity may not take a name another entity already has.
 * Case-insensitive — `Customer` and `customer` side by side read as one entity to a builder — and hidden
 * entities count, because their names are just as taken. Asked by the form's Save and by the entity chat.
 */
export function entityNameTaken(name: string, list: readonly Pick<Entity, 'name'>[]): boolean {
  const wanted = name.toLowerCase();
  return list.some((e) => (e.name ?? '').toLowerCase() === wanted);
}

// ── Field types ─────────────────────────────────────────────────────────────────────────────────────

/** ⚠ UUID, AUTO_INCREMENT and JSONB are system column types, never offered (owner, 2026-09-17). */
export const OFFERED_FIELD_TYPES: readonly string[] = [
  'STRING', 'NUMBER', 'BOOLEAN', 'ARRAY_OF_OBJECT', 'ENTITY_REFERENCE', 'ATTACHMENT', 'TIMESTAMP', 'GEOMETRY',
];

/** The plain value types — what a predefined (sample) row can hold. */
export const SCALAR_FIELD_TYPES: readonly string[] = ['STRING', 'NUMBER', 'BOOLEAN', 'TIMESTAMP'];

export function isScalarFieldType(type: string): boolean {
  return SCALAR_FIELD_TYPES.includes(type);
}

/**
 * The types the Field Type list offers. A local entity gets no reference and no list of items.
 * ⚠ AN EDITED FIELD ALWAYS SEES ITS OWN STORED TYPE (owner, 2026-09-17): a field saved as a type no longer
 * offered (`JSONB`, `UUID`, `AUTO_INCREMENT`) is appended, so it never opens blank and is never re-saved
 * as something else. Pass the STORED type, not the one being edited.
 */
export function offeredFieldTypes(status: string | undefined, storedType: string | null): string[] {
  const offered = status === 'LOCAL'
    ? OFFERED_FIELD_TYPES.filter((t) => t !== 'ENTITY_REFERENCE' && t !== 'ARRAY_OF_OBJECT')
    : [...OFFERED_FIELD_TYPES];
  return storedType && !offered.includes(storedType) ? [...offered, storedType] : offered;
}

// ── Statuses ────────────────────────────────────────────────────────────────────────────────────────

/** Statuses whose table has been asked for (glossary: Finalized). New and Local are not finalized. */
export const FINALIZED_STATUSES: readonly string[] = ['READY', 'PENDING', 'SYNCED', 'ERROR'];

export function isFinalized(status: string | undefined): boolean {
  return FINALIZED_STATUSES.includes(status ?? '');
}

/**
 * Whether every setting of an existing field may change, or only its default and description.
 * Everything while the entity is New, or before its first save (no id yet — a local entity being created
 * is editable until saved). This is the form's `detailEditFlag`. It is what keeps a rename or a type change
 * away from a table that already exists (ADR 0009).
 */
export function canEditAllFieldSettings(entity: { id?: string; status?: string }): boolean {
  return !entity.id || entity.status === 'NEW';
}

/**
 * The settings ANY field may still change once `canEditAllFieldSettings` is false. A Timestamp adds its
 * default mode — ask `lockedFieldEditableSettings(field)`, never this list directly.
 * Was (2026-10-02): "⚠ N8 — `defaultMode` is NOT in the list: that is today's behaviour, not a decision."
 */
export const LOCKED_FIELD_EDITABLE_SETTINGS = ['defaultValue', 'description'] as const;

/**
 * N8 FIXED (owner Q35, 2026-10-03): the mode (now / fixed / none) is part of a Timestamp's default, so a
 * finalized Timestamp field may change it too. It belongs to Timestamps only — a system timestamp
 * (`systemCreatedAt`, `systemUpdatedAt`, any `isSystemField` name) keeps the mode the platform gave it,
 * because Postgres owns its value.
 */
// Was (final review, 2026-10-03 — type-only signature let the Foblex detail graph (N4) save a mode
// change onto a system timestamp since N8; the caller needs the field's name too):
// export function lockedFieldEditableSettings(type: string): readonly string[] {
//   return type === 'TIMESTAMP' ? [...LOCKED_FIELD_EDITABLE_SETTINGS, 'defaultMode'] : LOCKED_FIELD_EDITABLE_SETTINGS;
// }
export function lockedFieldEditableSettings(field: { type: string; name: string }): readonly string[] {
  return field.type === 'TIMESTAMP' && !isSystemField(field.name)
    ? [...LOCKED_FIELD_EDITABLE_SETTINGS, 'defaultMode']
    : LOCKED_FIELD_EDITABLE_SETTINGS;
}

// ── System fields ───────────────────────────────────────────────────────────────────────────────────

/** Never deletable, at any status. */
export const PROTECTED_FIELD_NAMES: readonly string[] = ['id', 'id_auto_increment', 'parent_id'];

export function isProtectedField(name: string): boolean {
  return PROTECTED_FIELD_NAMES.includes(name);
}

/**
 * Read-only in the schema view: the protected three AND any "system"-word field (`systemCreatedAt`).
 * ⚠ N4 — this is the X6 graph's rule (the default engine). The Foblex graph treats only the three as system.
 */
export function isSystemField(name: string): boolean {
  return isProtectedField(name) || isSystemVariableKey(name);
}

// ── Reference targets ───────────────────────────────────────────────────────────────────────────────

/**
 * What a reference field may point at: finalized TABLE entities, in list order. The picker lists the entity
 * being edited first (referenceTargetOptions).
 * ⚠ N6 — hidden entities are NOT filtered; the form offers them.
 * List-of-items sub-tables were offered once; that code is commented out in the component and stays off.
 */
export function referenceTargets(
  list: readonly Pick<Entity, 'id' | 'name' | 'type' | 'status'>[],
): { name: string; id: string }[] {
  const out: { name: string; id: string }[] = [];
  for (const e of list) {
    if (isFinalized(e.status) && e.type === 'TABLE') out.push({ name: e.name, id: e.id });
  }
  return out;
}

/**
 * What the reference picker offers: every finalized target, the open entity FIRST and labelled
 * "(this entity)" — an entity may reference itself. Matched by id, so a different entity that shares the
 * open one's name is not hidden.
 */
export function referenceTargetOptions(
  targets: readonly { name: string; id: string }[],
  openEntityId: string,
): { name: string; id: string; label: string }[] {
  const self = targets.filter((t) => t.id === openEntityId).map((t) => ({ ...t, label: `${t.name} (this entity)` }));
  const others = targets.filter((t) => t.id !== openEntityId).map((t) => ({ ...t, label: t.name }));
  return [...self, ...others];
}

// Was (2026-10-16, self-reference — the picker hid the open entity, by name):
// /**
//  * `referenceTargets`, minus the entity being edited — the picker's own exclusion.
//  * ⚠ N9 — excludes by NAME (as the template did), so with N3 duplicate names it also hides a different
//  * entity that happens to share the open entity's name.
//  */
// export function referenceTargetOptions(
//   targets: readonly { name: string; id: string }[],
//   openEntityName: string,
// ): { name: string; id: string }[] {
//   return targets.filter((t) => t.name !== openEntityName);
// }

// ── Composed checks — the form's sequences, for the entity chat ─────────────────────────────────────

/**
 * A new field's name the way the form's Add ends up with it: Add's cleanup (⚠ N1 — drops `_`), then Add's
 * check. One call, so the chat cannot check before cleaning or clean the wrong way.
 */
export function prepareNewFieldName(
  raw: string,
  siblings: readonly Pick<TypeData, 'name'>[],
): { name: string; check: NameCheck } {
  const name = cleanFieldNameOnAdd(raw);
  return { name, check: checkNewFieldName(name, siblings) };
}

/**
 * A new entity's name the way the form ends up with it: the name box's cleanup (⚠ N2 — a name the chat
 * sets was never typed, so it must pass this first), Save's cleanup, Save's check, then the
 * name-in-use check (N3, owner Q34).
 */
export function prepareNewEntityName(
  raw: string,
  list: readonly Pick<Entity, 'name'>[],
): { name: string; check: NameCheck } {
  const name = cleanEntityNameOnSave(cleanEntityNameAsTyped(raw));
  const check = checkEntityNameOnSave(name);
  if (!check.ok) return { name, check };
  return { name, check: entityNameTaken(name, list) ? refuse(ENTITY_NAME_TAKEN_MESSAGE) : OK };
}

/**
 * Whether two entity names are the same once the form's cleanup has run on both (plan 4a final review, I1) —
 * the model may write `customer_account` where the page saved `customeraccount`. Empty never matches.
 */
export function sameEntityName(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const key = (x: string): string => prepareNewEntityName(x, []).name.toLowerCase();
  return key(a) === key(b);
}

/**
 * Whether the page lets a builder open an existing field for editing: not a system field, and not a list
 * of items (X6 never opens one; Foblex lifts lists into their own nodes). A list's own fields are edited
 * one by one. Retyping a plain field INTO a list is still an edit of that plain field.
 */
export function canOpenFieldForEdit(field: { name: string; type: string }): boolean {
  return !isSystemField(field.name) && field.type !== 'ARRAY_OF_OBJECT';
}

// ── Sample rows ─────────────────────────────────────────────────────────────────────────────────────

/** The fields a sample row has a cell for: the plain types, never the protected three (the form's `predefinedFields`). */
export function sampleRowFields(data: readonly TypeData[] | undefined): TypeData[] {
  return (data ?? []).filter((f) => isScalarFieldType(f.type) && !isProtectedField(f.name));
}

/** The page offers sample rows (its Predefined button) only on a LOCAL entity with at least one such field. */
export function canHaveSampleRows(entity: Pick<Entity, 'status' | 'data'>): boolean {
  return entity.status === 'LOCAL' && sampleRowFields(entity.data).length > 0;
}

// ── Role permissions ────────────────────────────────────────────────────────────────────────────────

/**
 * The roles taken off an entity since its permissions were loaded (plan 4b, Task 1). Save must wipe their
 * grant for that entity, because it writes only the roles still listed. The old entity screen did this
 * (EntityComponent.saveRolePermissions); the port to the entity list dropped it, so a removed role kept
 * its access.
 */
export function rolesRemovedSince(persisted: readonly string[], current: readonly string[]): string[] {
  return persisted.filter((id) => !current.includes(id));
}

/**
 * A permission change applied to a role's row AS IT IS NOW (null = the role is not on the entity) — plan 4b
 * fix round 1. The new row, or null when the role comes off. Only the approved permissions change.
 */
export function applyPermissionChange(current: Crud | null, change: PermissionChange): Crud | null {
  if (change.op === 'remove_role') return null;
  const next: Crud = { create: false, read: false, update: false, delete: false, ...(current ?? {}) };
  for (const p of change.permissions) next[p] = change.op === 'grant';
  return next;
}

// Was (2026-10-04, plan 4d Q45 — the rule moved to one shared file the Roles tab asks too): the function above.
// /**
//  * The built-in admin (plan 4c, spec Q37; glossary: Built-in admin): the server's reserved `admin` role, which
//  * may do everything with every entity and whose permissions can't be changed — the server refuses (409). Asked
//  * by the page's Add Role list and by the entity chat, so neither offers what can never be saved.
//  */
// export function isFullAccessRole(role: { id: string; reserved?: boolean }): boolean {
//   return role.reserved === true || role.id === 'admin';
// }
export { isFullAccessRole } from '../../../../services/role-rules';
